import { useState } from "react";

import {
  useDeleteManagedUser,
  useUpdateManagedUser,
} from "@/hooks/useAdminManageUsers";
import { useAppNotification } from "@/hooks/useAppNotification";
import type { UpdateManagedUserPayload } from "@/services/adminManageService";
import {
  buildDeactivateFailedNotification,
  buildUpdateFailedNotification,
  buildUserDeactivatedNotification,
  buildUserSettingsUpdatedNotification,
} from "./adminNotificationConfigs";
import {
  buildManagedUserUpdateRequests,
  getAdminUserDisplayName,
} from "./adminRolesPermissionsMappers";
import type { AdminUser } from "./adminUsersAccessData";
import type { EditUserSettingsSubmitPayload } from "./EditUserSettingsModal";

export type DeactivationIntent = {
  user: AdminUser;
  variant: "direct" | "revoke-all-trials";
  updateRequests: UpdateManagedUserPayload[];
};

export function useManagedUserEditFlow() {
  const updateManagedUserMutation = useUpdateManagedUser();
  const deleteManagedUserMutation = useDeleteManagedUser();
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();

  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editErrorMessage, setEditErrorMessage] = useState<string | null>(null);
  const [deactivationIntent, setDeactivationIntent] =
    useState<DeactivationIntent | null>(null);
  const [deactivateErrorMessage, setDeactivateErrorMessage] = useState<
    string | null
  >(null);

  const openEditModal = (user: AdminUser) => {
    setEditErrorMessage(null);
    setEditingUser(user);
  };

  const closeEditModal = () => setEditingUser(null);

  const requestDeactivation = (user: AdminUser) => {
    setDeactivateErrorMessage(null);
    setDeactivationIntent({
      user,
      variant: "direct",
      updateRequests: [
        {
          role_assignment: "admin",
          client_admin: false,
        },
      ],
    });
  };

  const closeDeactivationIntent = () => setDeactivationIntent(null);

  const handleEditUserSubmit = async (
    payload: EditUserSettingsSubmitPayload,
  ) => {
    if (!editingUser) {
      return;
    }

    setEditErrorMessage(null);

    try {
      // Backend rejects empty name strings, so blank fields are omitted
      // (email-only invitees have no names until onboarding).
      const profileFields = {
        ...(payload.firstName ? { first_name: payload.firstName } : {}),
        ...(payload.lastName ? { last_name: payload.lastName } : {}),
      };
      const requests = buildManagedUserUpdateRequests(
        payload,
        profileFields,
        editingUser,
      );

      if (payload.retainedPermissions.length === 0 && !payload.clientAdmin) {
        setDeactivateErrorMessage(null);
        setEditingUser(null);
        setDeactivationIntent({
          user: editingUser,
          variant: "revoke-all-trials",
          updateRequests: requests,
        });
        return;
      }

      if (payload.retainedPermissions.length > 0) {
        await Promise.all(
          payload.revokedTrialIds.map((trialId) =>
            deleteManagedUserMutation.mutateAsync({
              userId: editingUser.id,
              options: { trial: trialId },
            }),
          ),
        );
      }

      await Promise.all(
        requests.map((request) =>
          updateManagedUserMutation.mutateAsync({
            userId: editingUser.id,
            payload: request,
          }),
        ),
      );

      openSuccessNotification(buildUserSettingsUpdatedNotification());
      setEditingUser(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update user settings. Please try again.";

      setEditErrorMessage(errorMessage);
      openErrorNotification(buildUpdateFailedNotification(errorMessage));
    }
  };

  // TODO: Replace with a dedicated deactivation endpoint once the backend
  // contract for full tenant deactivation exists. Until then the "admin"
  // role_assignment shorthand revokes all environment trial grants and
  // client_admin: false clears admin status in a single PATCH.
  const handleDeactivateUser = async () => {
    if (!deactivationIntent) {
      return;
    }

    setDeactivateErrorMessage(null);

    try {
      await Promise.all(
        deactivationIntent.updateRequests.map((request) =>
          updateManagedUserMutation.mutateAsync({
            userId: deactivationIntent.user.id,
            payload: request,
          }),
        ),
      );

      openSuccessNotification(
        buildUserDeactivatedNotification(
          getAdminUserDisplayName(deactivationIntent.user),
        ),
      );
      setDeactivationIntent(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `There was an error deactivating ${getAdminUserDisplayName(
              deactivationIntent.user,
            )}. Please try again.`;

      setDeactivateErrorMessage(errorMessage);
      openErrorNotification(
        buildDeactivateFailedNotification(
          getAdminUserDisplayName(deactivationIntent.user),
          errorMessage,
        ),
      );
    }
  };

  return {
    contextHolder,
    editingUser,
    editErrorMessage,
    deactivationIntent,
    deactivateErrorMessage,
    isEditUserSubmitting:
      updateManagedUserMutation.isPending ||
      deleteManagedUserMutation.isPending,
    isDeactivateSubmitting: updateManagedUserMutation.isPending,
    openEditModal,
    closeEditModal,
    requestDeactivation,
    closeDeactivationIntent,
    handleEditUserSubmit,
    handleDeactivateUser,
  };
}
